import { BroadcastAdminDTO, BroadcastState, ExpressDispatchClientState } from 'models/deal'
import { useCallback, useMemo } from 'react'

import { CardColor } from 'constants/assignment'
import { DealDTOIsAdministratorDTO } from 'utils/typeguards'
import { ISODateString } from 'models/misc'
import constate from 'constate'
import { getDateTimeStringSafe } from 'utils/time'
import moment from 'moment-timezone'
import { useDynamicNow } from 'utils/hooks'
import { useExpressBroadcastRefresh } from './useExpressBroadcastRefresh'
import { useGalleryDeal } from '../../_main/contexts'
import { useRevokeExpressBroadcast } from 'dataQueries'
import { useTimezone } from 'components/contexts/timezone.context'

const MINUTE_MS = 60_000

/** Card colour for the broadcast's state; overdue reads as a problem */
export function broadcastColor(broadcast: BroadcastAdminDTO): CardColor {
  if (broadcast.overdue) return CardColor.RED
  switch (broadcast.state) {
    case BroadcastState.ACCEPTED:
      return CardColor.GREEN
    case BroadcastState.EXPIRED:
      return CardColor.RED
    case BroadcastState.REVOKED:
      return CardColor.ORANGE
    case BroadcastState.OPEN:
    default:
      return CardColor.BLUE
  }
}

export const [ExpressBroadcastCardContextProvider, useExpressBroadcastCard] = constate(() => {
  const { dealData, dealId, order } = useGalleryDeal()
  const { userTimezone } = useTimezone()
  const dynamicNow = useDynamicNow()
  const revokeBroadcastMutation = useRevokeExpressBroadcast()

  const adminDeal = useMemo(() => !!dealData && DealDTOIsAdministratorDTO(dealData) ? dealData : null, [dealData])
  const expressDispatch = adminDeal?.expressDispatch ?? null
  const expressBroadcast = adminDeal?.expressBroadcast ?? null
  const timezone = adminDeal?.timezone || userTimezone

  // Device clock skew, measured when the order arrived rather than when it renders (it may come from cache)
  const fetchedAt = !!order && 'dataUpdatedAt' in order ? order.dataUpdatedAt : 0
  const serverOffsetMs = useMemo(() => {
    if (!expressDispatch) return 0
    return moment(expressDispatch.serverTime).valueOf() - (fetchedAt || Date.now())
  }, [expressDispatch, fetchedAt])

  const minutesLeft = useMemo(() => {
    if (!expressDispatch) return 0
    const msLeft = moment(expressDispatch.expiresAt).valueOf() - (dynamicNow.valueOf() + serverOffsetMs)
    return Math.max(0, Math.ceil(msLeft / MINUTE_MS))
  }, [dynamicNow, expressDispatch, serverOffsetMs])

  const countdownOver = !!expressDispatch && minutesLeft <= 0
  const searchLive = expressDispatch?.state === ExpressDispatchClientState.SEARCHING || expressBroadcast?.state === BroadcastState.OPEN
  useExpressBroadcastRefresh(dealId, searchLive, countdownOver)

  // The read may predate the deadline; the countdown decides until the next read lands
  const canRevoke = !!expressBroadcast?.revocable && !countdownOver
  const broadcastAcceptsAnswers = expressBroadcast?.state === BroadcastState.OPEN && !expressBroadcast.overdue && !countdownOver

  const cardColor: CardColor = useMemo(() => {
    switch (expressDispatch?.state) {
      case ExpressDispatchClientState.CREATIVE_FOUND:
        return CardColor.GREEN
      case ExpressDispatchClientState.NOBODY_FOUND:
      case ExpressDispatchClientState.CANCELLED:
        return CardColor.RED
      case ExpressDispatchClientState.HANDLED_BY_TEAM:
        return CardColor.ORANGE
      case ExpressDispatchClientState.SEARCHING:
      default:
        return CardColor.BLUE
    }
  }, [expressDispatch?.state])

  const formatDateTime = useCallback(
    (dateTime: ISODateString) => getDateTimeStringSafe(dateTime, { timezone }) ?? dateTime,
    [timezone]
  )

  return {
    broadcastAcceptsAnswers,
    canRevoke,
    cardColor,
    dealId,
    expressBroadcast,
    expressDispatch,
    formatDateTime,
    minutesLeft,
    revokeBroadcastMutation,
    timezone,
  }
})
